import type {
  ActionJustificatif,
  CadenceConnexion,
  Certitude,
  ExecutantConsigne,
  NatureOperation,
  SignalJustificatif,
  StatutJustificatif,
  StatutBrouillon,
  StatutConsigne,
  TypeBrouillon,
  EtatEnvoi,
  Frequence,
  MoyenPaiement,
  StatutPaiement,
  NiveauRole,
  RegimeImpot,
  RegimeTva,
  RoleTiers,
  SourceCompte,
  StatutEcheance,
  StatutProjet,
  IssueEnvoi,
  StatutTache,
  EspaceDrive,
  ModeTransmission,
  StatutPiece,
  TransmissionPiece,
  RangementPiece,
  OrdreDossier,
  Rubrique,
  EtatAcheminement,
  TypePiece,
  TypeContrat,
  TypeEntite,
  EtatSoin,
  OrigineSoin,
  PayeurSante,
  RolePieceSoin,
  StatutRemboursement,
  EtatLigneRefacturation,
  EtatLotRefacturation,
} from '@admin-pmvb/core'
import { formaterJour } from '@admin-pmvb/core'

// Libellés d'interface des valeurs du domaine.

export const LIBELLES_TYPE_ENTITE: Record<TypeEntite, string> = {
  societe: 'Société',
  activite_nom_propre: 'Activité en nom propre',
  foyer: 'Foyer',
}

export const LIBELLES_REGIME_TVA: Record<RegimeTva, string> = {
  reel_normal_mensuel: 'Réel normal, mensuel',
  reel_normal_trimestriel: 'Réel normal, trimestriel',
  reel_simplifie: 'Réel simplifié',
  franchise: 'Franchise en base',
  non_assujetti: 'Non assujetti',
}

export const LIBELLES_REGIME_IMPOT: Record<RegimeImpot, string> = {
  is: 'Impôt sur les sociétés',
  ir: 'Impôt sur le revenu',
  lmnp_reel: 'LMNP au réel',
  lmnp_micro: 'LMNP micro-BIC',
}

export const LIBELLES_NIVEAU_ROLE: Record<NiveauRole, string> = {
  administrateur: 'Administrateur',
  associe: 'Associé',
}

export const LIBELLES_ROLE_TIERS: Record<RoleTiers, string> = {
  cabinet: 'Cabinet comptable',
  fournisseur: 'Fournisseur',
  client: 'Client',
  locataire: 'Locataire',
  administration: 'Administration',
  gestionnaire: 'Gestionnaire',
  organisme_sante: 'Assurance maladie ou mutuelle',
  particulier: 'Particulier',
}

export const LIBELLES_TYPE_CONTRAT: Record<TypeContrat, string> = {
  abonnement: 'Abonnement',
  pret: 'Prêt',
  assurance: 'Assurance',
  gestion: 'Gestion',
}

export const LIBELLES_FREQUENCE: Record<Frequence, string> = {
  mensuelle: 'Mensuelle',
  bimestrielle: 'Bimestrielle',
  trimestrielle: 'Trimestrielle',
  semestrielle: 'Semestrielle',
  annuelle: 'Annuelle',
  ponctuelle: 'Ponctuelle',
}

export const LIBELLES_SOURCE_COMPTE: Record<SourceCompte, string> = {
  csv: 'Import CSV',
  dsp2: 'Agrégateur bancaire',
}

export const LIBELLES_STATUT_ECHEANCE: Record<StatutEcheance, string> = {
  a_venir: 'À venir',
  en_preparation: 'En préparation',
  faite: 'Faite',
  sans_objet: 'Sans objet',
}

export const LIBELLES_STATUT_PAIEMENT: Record<StatutPaiement, string> = {
  prevu: 'Prévu',
  programme: 'Programmé',
  partiel: 'Partiel',
  paye: 'Payé',
  rapproche: 'Rapproché',
  rejete: 'Rejeté',
  annule: 'Annulé',
  en_litige: 'En litige',
}

export const LIBELLES_STATUT_TACHE: Record<StatutTache, string> = {
  a_faire: 'À faire',
  en_cours: 'En cours',
  en_attente_tiers: 'En attente d’un tiers',
  faite: 'Faite',
  annulee: 'Annulée',
}

/** Ce que devient la tâche quand son envoi part. */
export const LIBELLES_ISSUE_ENVOI: Record<IssueEnvoi, string> = {
  terminer: 'Terminer la tâche',
  attendre_reponse: 'Attendre la réponse (tâche en attente)',
  rien: 'Ne rien changer à la tâche',
}

export const LIBELLES_STATUT_PROJET: Record<StatutProjet, string> = {
  en_cours: 'En cours',
  termine: 'Terminé',
  abandonne: 'Abandonné',
}

export const LIBELLES_TYPE_PIECE: Record<TypePiece, string> = {
  facture: 'Facture',
  avis: 'Avis',
  courrier: 'Courrier',
  accuse_depot: 'Accusé de dépôt',
  releve: 'Relevé',
  contrat: 'Contrat',
  fiche_paie: 'Fiche de paie',
  autre: 'Autre',
}

export const LIBELLES_STATUT_PIECE: Record<StatutPiece, string> = {
  recue: 'Reçue, en cours',
  extraite: 'Lue, en cours',
  rattachee: 'Rattachée',
  a_verifier: 'À vérifier',
  doublon: 'Doublon',
  ignoree: 'Rejetée',
  incluse: 'Dans le fichier d’une autre pièce',
}

export const LIBELLES_TRANSMISSION: Record<TransmissionPiece, string> = {
  a_faire: 'Envoi au cabinet en cours',
  faite: 'Envoyée au cabinet',
  en_attente: 'Adresse de collecte à renseigner',
  sans_objet: '',
  a_decider: 'Réglée par la société ou avancée ?',
  incertain: 'Envoi au cabinet interrompu : à vérifier',
  manuelle: 'À déposer à la main chez le cabinet',
}

/** Rubriques du plan de classement, comme dans un dossier de révision. */
export const LIBELLES_RUBRIQUE: Record<Rubrique, string> = {
  juridique: 'Juridique (statuts, PV, Kbis)',
  etat_civil: 'État civil et identité',
  contrats: 'Contrats et assurances',
  baux: 'Baux et états des lieux',
  financements: 'Emprunts et financements',
  patrimoine: 'Patrimoine (actes, diagnostics)',
  ventes: 'Ventes et loyers',
  achats: 'Achats et frais',
  banque: 'Banque',
  fiscal: 'Fiscal et impôts',
  social: 'Social (paie, URSSAF, Pajemploi)',
  travaux: 'Travaux et immobilisations',
  courriers: 'Courriers et litiges',
  cloture: 'Clôture (comptes annuels, liasse)',
  revenus: 'Revenus',
  sante: 'Santé',
  famille: 'Famille (CAF, crèche, école)',
  logement: 'Logement',
}

/** Principe d'ordre d'un dossier du plan de classement : ce qu'il contient. */
export const LIBELLES_ORDRE_DOSSIER: Record<OrdreDossier, string> = {
  sous_dossiers: 'Ses sous-dossiers',
  fichiers: 'Les fichiers, directement',
  par_exercice: 'Un dossier par exercice',
  par_annee: 'Un dossier par année',
}

export const LIBELLES_RANGEMENT: Record<RangementPiece, string> = {
  a_faire: 'Rangement dans le Drive en cours',
  en_attente: 'Rangement dans le Drive en attente',
  fait: 'Rangée dans le Drive',
}

export const LIBELLES_ETAT_ACHEMINEMENT: Record<EtatAcheminement, string> = {
  en_lecture: 'En lecture',
  a_verifier: 'À vérifier',
  a_decider: 'Décision à prendre',
  drive_echec: 'Drive en échec',
  tiime_echec: 'Envoi au cabinet en échec',
  en_attente: 'En attente d’un réglage',
  en_cours: 'En cours',
  partiel: 'En partie acheminée',
  termine: 'Terminée',
  doublon: 'Doublon',
  rejetee: 'Rejetée',
  a_valider: 'Version remplie à valider',
}

export const LIBELLES_MODE_TRANSMISSION: Record<ModeTransmission, string> = {
  aucune: 'Rien à envoyer (dépôt au cabinet)',
  mail: 'Envoi par mail à l’adresse de collecte',
  tiime: 'Dépôt direct dans Tiime (agent du Mac)',
  dougs: 'Dépôt direct dans Dougs (agent du Mac)',
}

export const LIBELLES_ESPACE_DRIVE: Record<EspaceDrive, string> = {
  famille: 'Drive familial',
  flow_design: 'Drive Flow Design',
}

/** Provenance lisible : la boîte d'arrivée quand elle est connue, sinon la source. */
export function provenancePiece(p: { source: string; boite: string | null }): string {
  return p.source === 'gmail' && p.boite ? p.boite : (LIBELLES_SOURCE_PIECE[p.source] ?? p.source)
}

export const LIBELLES_SOURCE_PIECE: Record<string, string> = {
  gmail: 'admin@',
  depot: 'Dépôt',
  plateforme: 'Plateforme agréée',
  mcp: 'Assistant',
  photo: 'Photo (téléphone)',
  remplissage: 'Version remplie (assistant)',
}

/** État d'un envoi photo dans « Mes envois ». */
export const LIBELLES_ETAT_ENVOI: Record<EtatEnvoi, string> = {
  recu: 'Reçu',
  en_lecture: 'En lecture',
  range: 'Rangé',
  classe: 'Classé',
  a_verifier: 'À vérifier',
  deja_recu: 'Déjà reçu',
  ignore: 'Rejeté',
  transmis: 'Transmis aux administrateurs',
}

export const LIBELLES_MOYEN: Record<MoyenPaiement, string> = {
  prelevement: 'Prélèvement',
  virement_emis: 'Virement émis',
  loyer: 'Loyer',
  facture_client: 'Facture client',
  carte: 'Carte',
  cheque: 'Chèque',
  autre: 'Autre',
}

export const LIBELLES_RETARD = {
  non_concerne: '',
  dans_les_temps: '',
  en_retard: 'En retard',
  releve_manquant: 'Relevé manquant',
} as const

export const LIBELLES_CERTITUDE: Record<Certitude, string> = {
  fixe: 'Date fixe',
  fenetre: 'Fenêtre',
  a_confirmer: 'À confirmer',
}

export const LIBELLES_UNITE_PERIODE: Record<'mois' | 'trimestre' | 'annee', string> = {
  mois: 'Mois',
  trimestre: 'Trimestre',
  annee: 'Année',
}

export const LIBELLES_ACTION: Record<string, string> = {
  generer: 'Génération',
  marquer_paye: 'Marqué payé',
  demarquer_paye: 'Payé décoché',
  programmer: 'Programmé',
  couverture: 'Couverture bancaire',
  rejet: 'Rejet',
  importer_releve: 'Relevé importé',
  rapprocher: 'Rapprochement',
  rapprocher_manuellement: 'Rapprochement manuel',
  ignorer: 'Ignorée',
  rejeter: 'Rejetée',
  annuler: 'Annulation',
  terminer: 'Terminée',
  rouvrir: 'Rouverte',
  marquer_fait: 'Marquée faite',
  sans_objet: 'Sans objet',
  defaire: 'Défaite',
  creer_lien_ical: 'Lien d’agenda créé',
  revoquer_lien_ical: 'Lien d’agenda révoqué',
  creer: 'Création',
  modifier: 'Modification',
  supprimer: 'Suppression',
  archiver: 'Archivage',
  desarchiver: 'Désarchivage',
  prendre: 'Prise en charge',
  affecter: 'Affectation',
  commenter: 'Commentaire',
  preparer: 'En préparation',
  demarrer: 'Démarrée',
  attendre_tiers: 'En attente d’un tiers',
  reprendre: 'Reprise',
  recevoir: 'Reçue',
  extraire: 'Lue',
  corriger: 'Lecture corrigée à la main',
  doublon: 'Doublon',
  a_verifier: 'À vérifier',
  rattacher: 'Rattachée',
  ranger: 'Rangée dans le Drive',
  classer: 'Classée',
  deplacer: 'Déplacée dans le Drive',
  transmettre: 'Envoyée au cabinet',
  decider_reglement: 'Règlement indiqué (société ou avance)',
  regler_par_debit: 'Réglée par la société d’après son débit (décidée seule)',
  indiquer_payeur: 'Payeur indiqué (carte, refacturation)',
  factures_manquantes: 'Factures manquantes signalées (commande)',
  // La tâche d'un collecteur (décision du 02/11/2026).
  tache_collecteur: 'Tâche du collecteur ouverte',
  fermee_par_collecte: 'Fermée par le passage réussi du collecteur',
  reevaluer_transmission: 'Envoi au cabinet recalculé',
  transmission_incertaine: 'Envoi au cabinet interrompu',
  doublon_transmission: 'Pas envoyée : déjà chez le cabinet',
  acheminement_arrivee: 'Arrivée dans Tiime (vérifié)',
  acheminement_renvoyer: 'À renvoyer au cabinet',
  acheminement_reessayer: 'Nouvel essai demandé',
  acheminement_deposee_a_la_main: 'Déposée à la main chez le cabinet',
  fixer_montant: 'Montant fixé par une pièce',
  preuve: 'Preuve jointe',
  joindre_echeance: 'Avis joint à l’échéance déjà faite',
  relancer: 'Relancée',
  connecter_banque: 'Accès bancaire donné',
  reconnecter_banque: 'Accès bancaire renouvelé',
  deconnecter_banque: 'Accès bancaire retiré',
  lier_compte_banque: 'Relevé par l’agrégateur bancaire',
  delier_compte_banque: 'Rendu à l’import CSV',
  relever_banque: 'Relève bancaire',
  renouvellement_banque: 'Accès bancaire à renouveler',
  lier_piece: 'Pièce liée',
  delier_piece: 'Pièce retirée',
  assembler_dossier: 'Dossier assemblé',
  reverser: 'Reversement préparé',
  reporter_sante: 'Reportée sur les soins',
  remboursement_demander: 'Remboursement demandé',
  remboursement_recevoir: 'Remboursement reçu',
  remboursement_refuser: 'Remboursement refusé',
  remboursement_sans_objet: 'Remboursement sans objet',
  remboursement_rouvrir: 'Remboursement rouvert',
  demander_acces_temporaire: 'Accès à l’espace demandé',
  accorder_acces_temporaire: 'Accès à l’espace accordé',
  refuser_acces_temporaire: 'Accès à l’espace refusé',
  revoquer_acces_temporaire: 'Accès à l’espace révoqué',
  expirer_acces_temporaire: 'Accès à l’espace terminé',
  avertissement_depart: 'Départ incomplet : une règle attend',
  creer_guide: 'Guide créé',
  creer_plan: 'Plan de classement créé',
  regler_plafond_ia: 'Plafond IA réglé',
  regler_boite: 'Rangement de la boîte réglé',
  // Rapprochement des justificatifs chez le cabinet (Tiime) : passage à blanc, décisions et
  // validation ; puis, à l'étape d'écriture, l'ouverture et la coupure par un humain, et chaque
  // dépôt et chaque liaison de l'agent du Mac (des identifiants et des issues, jamais un montant).
  justificatifs_demande: 'Rapprochement des justificatifs demandé',
  dossier_justificatifs_retenu: 'Dossier du cabinet retenu par le SIREN',
  operations_relevees: 'Opérations relevées chez le cabinet',
  valider_passage_justificatifs: 'Passage à blanc validé',
  ouvrir_ecriture_justificatifs: 'Écriture des justificatifs ouverte',
  couper_ecriture_justificatifs: 'Écriture des justificatifs coupée',
  justificatif_deja_present: 'Reconnue déjà chez le cabinet',
  constat_dougs: 'Factures de Dougs constatées',
  justificatif_reserve: 'Dépôt chez le cabinet réservé',
  justificatif_remis: 'Fichier remis à l’agent du Mac',
  justificatif_depose: 'Déposée chez le cabinet',
  justificatif_relie: 'Reliée à son opération chez le cabinet',
  justificatif_echec: 'Dépôt ou liaison en échec',
  justificatif_orphelin: 'Déposée, liaison à vérifier',
  justificatif_adopte: 'Dépôt repris par un nouveau passage',
  justificatif_abandonne: 'Dépôt du justificatif abandonné',
  justificatif_incertain: 'Dépôt du justificatif interrompu',
  justificatif_arrive: 'Dépôt interrompu : arrivé chez le cabinet',
  justificatif_renvoye: 'Dépôt interrompu : à redéposer',
  justificatif_reservation_refusee: 'Dépôt refusé avant l’envoi',
  justificatif_remise_refusee: 'Fichier non remis à l’agent',
  justificatif_etape_refusee: 'Compte rendu de l’agent refusé',
  ecritures_arretees: 'Écritures du passage arrêtées',
  decider_justificatif: 'Justificatif tranché',
  justificatifs_a_trancher: 'Justificatifs à choisir',
  // Le pont entre une opération du cabinet et le débit du relevé (décision du 13/10/2026).
  rapprocher_par_choix: 'Rapprochement bancaire par le choix du justificatif',
  choisir_par_rapprochement: 'Justificatif choisi par le rapprochement bancaire',
  decision_reprise_justificatifs: 'Rapprochement bancaire repris dans les justificatifs',
  // Au foyer, plus aucune pièce en face du débit (décision du 30/10/2026).
  decision_sans_candidat: 'Rapprochement bancaire annulé : plus aucune pièce en face',
  justificatifs_a_fournir: 'Justificatifs à fournir',
  justificatifs_a_verifier: 'Justificatifs à vérifier',
  // Recherche du jour des pièces manquantes dans les mails (décision du 12/10/2026) : des comptes.
  recherche_mails_justificatifs: 'Pièces manquantes cherchées dans les mails',
  // « Chercher dans les mails » depuis le bilan : la demande, puis la recherche du worker.
  demander_recherche_mails: 'Recherche dans les mails demandée',
  recherche_mails_demandee: 'Recherche demandée menée dans les mails',
  // Nom du fournisseur d'un débit, lu par Claude dans le libellé (décision du 14/10/2026).
  nommer_fournisseur: 'Fournisseur nommé d’après le libellé bancaire',
  // Fiche du fournisseur proposée après un rapprochement (décision du 14/10/2026).
  proposer_fiche_fournisseur: 'Fiche du fournisseur à compléter proposée',
  completer_fiche_fournisseur: 'Fiche du fournisseur complétée',
  refuser_fiche_fournisseur: 'Fiche du fournisseur laissée telle quelle',
}

export const LIBELLES_OBJET: Record<string, string> = {
  entite: 'Entité',
  acces_temporaire: 'Accès temporaire',
  personne: 'Personne',
  role: 'Rôle',
  tiers: 'Tiers',
  bien: 'Bien',
  bail: 'Bail',
  contrat: 'Contrat',
  compte: 'Compte',
  regle: 'Règle',
  echeance: 'Échéance',
  paiement: 'Paiement',
  transaction: 'Transaction',
  rapprochement: 'Rapprochement',
  reglage: 'Réglage',
  tache: 'Tâche',
  piece: 'Pièce',
  acces_banque: 'Accès bancaire',
  beneficiaire: 'Membre du foyer',
  couverture: 'Couverture santé',
  soin: 'Soin',
  remboursement_sante: 'Remboursement santé',
  guide: 'Guide',
  boite: 'Boîte mail',
  connexion_portail: 'Connexion de portail',
  operation_portail: 'Opération chez le cabinet',
}

const MOIS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]
const MOIS_COURTS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
]

/** « 2026-10 » ou « 2026-10-15 » devient « octobre 2026 ». */
export function nomMois(jour: string): string {
  return `${MOIS[Number(jour.slice(5, 7)) - 1]} ${jour.slice(0, 4)}`
}

export function moisCourt(jour: string): string {
  return MOIS_COURTS[Number(jour.slice(5, 7)) - 1]!
}

/** Initiales d'un nom : « Marie-Violaine Boutet » devient « MVB ». */
export function initiales(nom: string): string {
  return nom
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((m) => m[0]!.toUpperCase())
    .join('')
}

/** Options d'un champ de choix à partir d'une table de libellés. */
export const LIBELLES_STATUT_CONSIGNE: Record<StatutConsigne, string> = {
  proposee: 'Proposée',
  active: 'Active',
  inactive: 'Inactive',
}

export const LIBELLES_EXECUTANT_CONSIGNE: Record<ExecutantConsigne, string> = {
  responsables: 'Les responsables (à prendre)',
  gestionnaire: 'Le gestionnaire du bail',
  tiers: 'Un tiers désigné',
}

export const LIBELLES_TYPE_BROUILLON: Record<TypeBrouillon, string> = {
  courriel: 'Courriel',
  courrier: 'Courrier',
  navigation: 'Agent navigateur',
  rendez_vous: 'Rendez-vous',
}

export const LIBELLES_STATUT_BROUILLON: Record<StatutBrouillon, string> = {
  brouillon: 'À relire',
  valide: 'Validé',
  fait: 'Fait',
  abandonne: 'Abandonné',
}

export function options<K extends string>(libelles: Record<K, string>) {
  return (Object.entries(libelles) as [K, string][]).map(([valeur, libelle]) => ({
    valeur,
    libelle,
  }))
}

/** « 2026-10-15 » devient « 15 oct. ». */
export function jourCourt(jour: string): string {
  const j = Number(jour.slice(8, 10))
  return `${j === 1 ? '1er' : j} ${moisCourt(jour)}`
}

/** « 2026-10-15 » devient « 15/10 ». */
export function jourMois(jour: string): string {
  return `${jour.slice(8, 10)}/${jour.slice(5, 7)}`
}

/** Montant signé : « +540,00 € » à encaisser, « −214,60 € » à payer. */
export function signe(sens: 'payer' | 'encaisser'): string {
  return sens === 'payer' ? '−' : '+'
}

// Santé du foyer

export const LIBELLES_ETAT_SOIN: Record<EtatSoin, string> = {
  a_venir: 'À venir',
  a_completer: 'À compléter',
  attente_secu: 'Attend la Sécu',
  attente_mutuelle: 'Attend la mutuelle',
  a_demander: 'Complément à demander',
  attente_autre_parent: 'Attend l’autre parent',
  a_reverser: 'À reverser',
  solde: 'Soldé',
  sans_suite: 'Sans suite',
}

/** Ton de la pastille : ce qui attend un geste en ambre, ce qui est fini en vert. */
export const TON_ETAT_SOIN: Record<EtatSoin, string> = {
  a_venir: 'ton-pointille',
  a_completer: 'ton-ambre',
  attente_secu: '',
  attente_mutuelle: '',
  a_demander: 'ton-ambre',
  attente_autre_parent: '',
  a_reverser: 'ton-ambre',
  solde: 'ton-ok',
  sans_suite: '',
}

export const LIBELLES_PAYEUR_SANTE: Record<PayeurSante, string> = {
  regime_obligatoire: 'Sécurité sociale',
  mutuelle: 'Mutuelle',
  autre_parent: 'Complément de l’autre parent',
}

export const LIBELLES_STATUT_REMBOURSEMENT: Record<StatutRemboursement, string> = {
  attendu: 'Attendu',
  demande: 'Demandé',
  recu: 'Reçu',
  refuse: 'Refusé',
  sans_objet: 'Sans objet',
}

export const LIBELLES_ROLE_PIECE_SOIN: Record<RolePieceSoin, string> = {
  facture: 'Facture',
  ordonnance: 'Ordonnance',
  decompte: 'Décompte',
  devis: 'Devis',
  autre: 'Autre',
}

export const LIBELLES_ORIGINE_SOIN: Record<OrigineSoin, string> = {
  manuelle: 'Saisi',
  mail: 'Repéré dans un mail',
  agenda: 'Repéré dans l’agenda',
  piece: 'Lu sur une pièce',
  portail: 'Lu sur le portail de la mutuelle',
}

/** Rythme de publication des documents d'une connexion de portail. */
export const LIBELLES_CADENCE: Record<CadenceConnexion, string> = {
  mensuelle: 'Mensuelle',
  bimestrielle: 'Tous les deux mois',
  trimestrielle: 'Trimestrielle',
  semestrielle: 'Semestrielle',
  annuelle: 'Annuelle',
  a_l_usage: 'À l’usage (une par achat ou course)',
  inconnue: 'Inconnue',
}

export const LIBELLES_ETAT_LIGNE_REFACTURATION: Record<EtatLigneRefacturation, string> = {
  a_refacturer: 'À refacturer',
  refacturee: 'Dans un lot',
  remboursee: 'Remboursée',
  abandonnee: 'Abandonnée',
}

export const TON_ETAT_LIGNE_REFACTURATION: Record<EtatLigneRefacturation, string> = {
  a_refacturer: 'ton-ambre',
  refacturee: '',
  remboursee: 'ton-ok',
  abandonnee: 'ton-pointille',
}

export const LIBELLES_ETAT_LOT_REFACTURATION: Record<EtatLotRefacturation, string> = {
  attendu: 'Remboursement attendu',
  rembourse: 'Remboursé',
  annule: 'Annulé',
}

export const TON_ETAT_LOT_REFACTURATION: Record<EtatLotRefacturation, string> = {
  attendu: 'ton-ambre',
  rembourse: 'ton-ok',
  annule: 'ton-pointille',
}

// Rapprochement des justificatifs chez le cabinet (bilan d'une connexion Tiime)

/** Nature d'une opération lue chez le portail du cabinet. */
export const LIBELLES_NATURE_OPERATION: Record<NatureOperation, string> = {
  carte: 'Carte',
  prelevement: 'Prélèvement',
  virement: 'Virement',
  autre: 'Autre',
}

/** Ce qui rapproche une pièce d'une opération, d'après la règle (jamais une décision). */
export const LIBELLES_SIGNAL_JUSTIFICATIF: Record<SignalJustificatif, string> = {
  montant: 'montant égal',
  origine: 'montant d’origine lu',
  change: 'change dans la bande',
  proche: 'montant proche',
  fournisseur: 'fournisseur nommé',
  numero: 'numéro de la pièce',
  echeance: 'échéance du prélèvement',
}

/** Ce que ferait (à blanc) ou fera (écriture ouverte) un passage pour une opération sûre. */
export const LIBELLES_ACTION_JUSTIFICATIF: Record<ActionJustificatif, string> = {
  deposer_et_relier: 'Déposer la pièce puis la relier',
  relier: 'Relier la pièce déjà déposée',
}

/** Ce que la fiche d'une pièce sait de sa ligne au registre des justificatifs. */
export type LigneDepotDirect = {
  portail: string
  /** Document créé chez le portail par l'agent ; null : reconnue, en cours. */
  documentRef: string | null
  /** Jour (AAAA-MM-JJ) de l'opération lue chez le portail ; null : plus lue. */
  operationLe: string | null
  /** Fichier remis à l'agent : une ligne `present` est un dépôt interrompu arrivé, pas reconnu. */
  remis?: boolean
}

const documentDe = (l: LigneDepotDirect) => (l.documentRef ? ` (document n° ${l.documentRef})` : '')
const operationDe = (l: LigneDepotDirect) =>
  l.operationLe && /^\d{4}-\d{2}-\d{2}$/.test(l.operationLe)
    ? `l’opération du ${formaterJour(l.operationLe)}`
    : 'son opération'

/** Où en est une pièce chez le portail du cabinet (sa ligne au registre des justificatifs). */
export const LIBELLES_DEPOT_DIRECT: Record<StatutJustificatif, (l: LigneDepotDirect) => string> = {
  present: (l) =>
    l.remis
      ? `Déposée dans ${l.portail} par l’agent : dépôt interrompu, arrivée constatée par un humain.`
      : `Déjà dans ${l.portail} : reconnue sur une opération déjà justifiée, jamais déposée.`,
  reserve: (l) => `Dépôt en cours dans ${l.portail}.`,
  depose: (l) =>
    `Déposée dans ${l.portail}${documentDe(l)}, pas encore reliée à ${operationDe(l)} : le prochain passage la relie si la correspondance est encore sûre.`,
  relie: (l) => `Déposée dans ${l.portail}${documentDe(l)}. Reliée à ${operationDe(l)}.`,
  incertain: (l) => `Dépôt interrompu : vérifier dans ${l.portail} s’il est arrivé.`,
  orphelin: (l) =>
    `Déposée dans ${l.portail}${documentDe(l)}, liaison à vérifier dans ${l.portail}.`,
  echec: (l) => `Dépôt dans ${l.portail} en échec.`,
  annule: (l) => `Dépôt dans ${l.portail} abandonné.`,
}

/**
 * Chemin type d'un dossier du plan, lisible : « Exercice AAAA/02 - Achats et frais » devient
 * « Exercice de la pièce › 02 - Achats et frais » (le dossier daté suit la date de la pièce).
 */
export function cheminLisible(chemin: string): string {
  return chemin
    .replace(/\bExercice AAAA\b/g, 'Exercice de la pièce')
    .replace(/\bAnnée AAAA\b/g, 'Année de la pièce')
    .split('/')
    .join(' › ')
}
